import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSiteFilterStore } from '../store/siteFilterStore.js'

/**
 * Syncs the `?site=1,2,3` URL search param with the siteFilterStore.
 *
 * Mount behaviour:
 *   - If URL has ?site=... AND store is empty, seed the store from the URL.
 *
 * Reactive behaviour:
 *   - When store siteIds changes, update URL param (replace, no history push).
 *   - When store empties, remove the param entirely.
 *
 * Infinite-loop guard:
 *   - Only update URL when URL value differs from store value.
 *   - Only update store when store value differs from URL value.
 */
export function useSiteFilterUrl(): void {
  const [searchParams, setSearchParams] = useSearchParams()
  const siteIds = useSiteFilterStore((s) => s.siteIds)
  const setSiteIds = useSiteFilterStore((s) => s.setSiteIds)

  // On mount: seed store from URL if URL has ?site=... and store is empty.
  useEffect(() => {
    const urlSite = searchParams.get('site')
    if (!urlSite) return
    const urlIds = urlSite.split(',').map((s) => s.trim()).filter(Boolean)
    if (urlIds.length === 0) return
    const current = useSiteFilterStore.getState().siteIds
    if (current.length === 0) {
      setSiteIds(urlIds)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // intentionally run only on mount

  // Reactive: when store siteIds changes, sync URL param.
  useEffect(() => {
    const storeVal = siteIds.length ? siteIds.join(',') : null
    const urlVal = searchParams.get('site') || null

    if (storeVal === urlVal) return // already in sync

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (storeVal) {
          next.set('site', storeVal)
        } else {
          next.delete('site')
        }
        return next
      },
      { replace: true },
    )
  }, [siteIds, searchParams, setSearchParams])
}
